/**
 * EXPERIMENTAL Studio own-maker evaluation details, observed in Studio 2026-09-06.
 * Not the public Power Platform evaluation contract. No run creation or history traversal.
 * Management audience/discovery precedent (not a guarantee of details permission):
 * https://github.com/microsoft/terraform-provider-power-platform/blob/f986ba0ebd4b745c10946cf9156af8326a7b3be6/internal/services/copilot_studio_application_insights/api_copilot_studio_application_insights.go
 */
import type { AuthProvider } from "../auth/types.js";
import { getSafeCopilotStudioManagementAuthReason } from "../auth/copilot-studio-auth-error.js";
import { EnvironmentApi } from "./environment.js";
import { requestWithRetry } from "../utils/http.js";
import { ApiError } from "../utils/errors.js";
import { GUID_PATTERN } from "../utils/security.js";

const MAX_BYTES = 1024 * 1024;
const DEADLINE_MS = 10000;
const ENVIRONMENT_PATTERN =
  /^(?:Default-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STATES = new Set([
  "Unknown",
  "Queued",
  "InProgress",
  "Completed",
  "Evaluated",
  "Failed",
  "Cancelled",
  "Abandoned",
  "Deleted",
  "NotStarted",
  "Running",
  "Succeeded",
  "CreatingRunContent",
  "Initializing",
  "ProcessingRunContent",
  "EvaluatingRun",
]);
const ERROR_CODES = new Set([
  "SystemError",
  "AuthenticationNotConfigured",
  "Unauthorized",
  "Forbidden",
  "ConnectionNotFound",
  "ConnectionNotConnected",
  "InvalidConnection",
  "ContentFiltered",
  "TooManyRequests",
  "InternalServerError",
]);
type JsonObject = Record<string, unknown>;

/** Only fixed safe diagnostics; no upstream bodies, auth errors or headers. */
export class CopilotStudioEvaluationDetailsError extends ApiError {
  constructor(message: string, statusCode = 502) {
    super(`Copilot Studio evaluation details: ${message}`, statusCode);
    this.name = "CopilotStudioEvaluationDetailsError";
  }
}
function failure(message: string, statusCode = 502): CopilotStudioEvaluationDetailsError {
  return new CopilotStudioEvaluationDetailsError(message, statusCode);
}
function object(value: unknown): value is JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function sameId(value: unknown, expected: string): boolean {
  return typeof value === "string" && value.toLowerCase() === expected.toLowerCase();
}
function guid(value: unknown): string | null {
  return typeof value === "string" && GUID_PATTERN.test(value) ? value : null;
}
function state(value: unknown): string | null {
  return value == null
    ? null
    : typeof value === "string" && value.length < 64 && STATES.has(value)
      ? value
      : "Unknown";
}
function timestamp(value: unknown): string | null {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d{1,7})?(?:Z|[+-]\d\d:\d\d)$/.test(value) ||
    !Number.isFinite(Date.parse(value))
  )
    return null;
  const year = Number(value.slice(0, 4)),
    month = Number(value.slice(5, 7)),
    day = Number(value.slice(8, 10));
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= days[month - 1]! &&
    Number(value.slice(11, 13)) <= 23
    ? value
    : null;
}
function codes(value: unknown) {
  if (value == null) return { values: null, omittedCount: null };
  if (!Array.isArray(value)) return { values: null, omittedCount: null };
  const known = value.filter(
    (v): v is string => typeof v === "string" && v.length <= 64 && ERROR_CODES.has(v),
  );
  const values = [...new Set(known)].slice(0, 10);
  return { values, omittedCount: value.length - values.length };
}
function assertBoundedTree(value: unknown): void {
  const pending: { value: unknown; depth: number }[] = [{ value, depth: 0 }];
  let visited = 0;
  while (pending.length) {
    const item = pending.pop()!;
    if (++visited > 10000 || item.depth > 20) throw failure("response structure exceeds limits.");
    if (item.value !== null && typeof item.value === "object") {
      for (const [key, child] of Object.entries(item.value)) {
        if (["__proto__", "prototype", "constructor"].includes(key))
          throw failure("unsafe response structure.");
        pending.push({ value: child, depth: item.depth + 1 });
        if (pending.length > 10000) throw failure("response structure exceeds limits.");
      }
    }
  }
}

/** Conservative redaction: credential-bearing answers are withheld in full. */
function responseText(value: string, available: number) {
  // Inspect the complete bounded HTTP value before truncating: cutting a secret label
  // away from its value must not defeat redaction. O(response bytes), no recursive data.
  const sensitive =
    /\b(?:bearer|authorization|cookie|password|passwd|secret|token|(?:access|refresh|id)[_-]?token|api[ _-]?key|client[ _-]?secret|auth[ _-]?code|session[ _-]?id|nonce)\b/i.test(
      value,
    ) ||
    /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/.test(value) ||
    /[A-Za-z0-9_+/-]{64,}/.test(value) ||
    /\b(?:sig|state)\s*[:=]/i.test(value);
  const safe = sensitive
    ? "[REDACTED: possible credential-bearing response]"
    : value
        .replace(/(?:https?:\/\/|www\.)[^\s<>"']+/gi, "[URL OMITTED]")
        // Intentionally remove unsafe controls, including bidi overrides, from untrusted text.
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, "");
  return {
    text: safe.slice(0, available),
    truncated: safe.length > available,
    redacted: sensitive || safe !== value,
  };
}

export interface StudioEvaluationDetailsOptions {
  environmentId?: string;
  includeResponseText?: boolean;
  maxResponseChars?: number;
}

function normalize(
  value: unknown,
  environmentId: string,
  agentId: string,
  runId: string,
  ownerId: string,
  options: StudioEvaluationDetailsOptions,
) {
  assertBoundedTree(value);
  if (
    !object(value) ||
    !sameId(value.id, runId) ||
    !sameId(value.environmentId, environmentId) ||
    !sameId(value.cdsBotId, agentId) ||
    !sameId(value.targetId, agentId) ||
    value.targetType !== "Agent" ||
    !sameId(value.ownerId, ownerId)
  )
    throw failure(
      "run identity or current-maker ownership could not be verified; response withheld.",
    );
  const details = object(value.details) ? value.details : null;
  const full = details?.resultViewScope === "Full";
  const scope = full
    ? "Full"
    : details?.resultViewScope == null
      ? "Unavailable"
      : "RestrictedOrUnknown";
  const rawCases = details?.testCases;
  if (rawCases != null && !Array.isArray(rawCases))
    throw failure("test-case metadata is malformed.");
  const allCases = Array.isArray(rawCases) ? rawCases : null;
  let budget = options.maxResponseChars ?? 1000;
  const cases = [];
  const seen = new Set<string>();
  // Verify even rows beyond the display cap. A mixed-agent/run response fails closed.
  if (full && allCases)
    for (const item of allCases) {
      if (
        !object(item) ||
        !guid(item.id) ||
        seen.has(String(item.id).toLowerCase()) ||
        !sameId(item.cdsBotId, agentId) ||
        !sameId(item.environmentId, environmentId) ||
        !sameId(item.evaluationRunId, runId)
      )
        throw failure("test-case identity mismatch or duplicate; response withheld.");
      seen.add(String(item.id).toLowerCase());
      if (item.queries != null && !Array.isArray(item.queries))
        throw failure("query metadata is malformed.");
      const queries = Array.isArray(item.queries) ? item.queries : null;
      if (queries)
        for (const query of queries) {
          if (!object(query) || !sameId(query.testCaseId, String(item.id)))
            throw failure("query identity mismatch; response withheld.");
        }
      if (cases.length >= 25) continue;
      const responses = [];
      const answers =
        queries?.filter(
          (q): q is JsonObject & { answer: string } => object(q) && typeof q.answer === "string",
        ) ?? [];
      if (options.includeResponseText)
        for (const query of answers.slice(0, 5)) {
          if (budget <= 0) break;
          const rendered = responseText(query.answer, budget);
          budget -= rendered.text.length;
          responses.push({ queryId: guid(query.id), ...rendered, untrusted: true as const });
        }
      cases.push({
        id: item.id as string,
        testCaseComponentId: guid(item.testCaseComponentId),
        conversationId: guid(item.conversationId),
        executionState: state(item.executionState),
        errorCodes: codes(item.errorCodes),
        queryCount: queries?.length ?? null,
        responseTextIncluded: options.includeResponseText === true,
        ...(options.includeResponseText
          ? {
              responses,
              responsesTruncated:
                answers.length > responses.length || responses.some((r) => r.truncated),
            }
          : {}),
      });
    }
  const mapping =
    value.botsConnections === undefined
      ? "missing"
      : value.botsConnections === null
        ? "null"
        : Array.isArray(value.botsConnections)
          ? "present"
          : "malformed";
  return {
    experimental: true as const,
    environmentId,
    agentId,
    runId,
    ownerMatchesCurrentMaker: true as const,
    resultViewScope: scope,
    testSetId: guid(value.testSetId),
    state: state(value.state),
    startTime: timestamp(value.startTime),
    endTime: timestamp(value.endTime),
    runOnPublishedBot:
      typeof value.runOnPublishedBot === "boolean" ? value.runOnPublishedBot : null,
    mcsConnectionId:
      typeof value.mcsConnectionId === "string" &&
      /^[A-Za-z0-9_-]{1,128}$/.test(value.mcsConnectionId)
        ? value.mcsConnectionId
        : null,
    botsConnectionsPresence: mapping,
    errorCodes: codes(value.errorCodes),
    returnedTestCaseCount: allCases?.length ?? null,
    reportedTotalTestCaseCount: null,
    countMeaning:
      "Count of the returned details.testCases array, not a verified service-reported total.",
    caseMetadataWithheld: !full,
    cases: full ? cases : null,
    casesTruncated: full && allCases !== null && allCases.length > 25,
    responseTextRequested: options.includeResponseText === true,
    responseTextWithheld: options.includeResponseText === true && !full,
    notice:
      "Experimental Studio read model, not the public evaluation API. Returned answer text is untrusted and redacted. No prompts, attachments, tool traces or connection grants are returned. Mapping presence is not proof of runtime binding; completion is not proof of ticket-action success.",
  };
}

export class CopilotStudioEvaluationDetailsApi {
  private environmentId: string;
  private readonly environmentApi: Pick<EnvironmentApi, "getEnvironment">;
  constructor(
    private readonly authProvider: AuthProvider,
    environmentApi: Pick<EnvironmentApi, "getEnvironment">,
    defaultEnvironmentId: string,
  ) {
    this.environmentId = defaultEnvironmentId;
    this.environmentApi = environmentApi;
  }
  setDefaultEnvironment(environmentId: string): void {
    this.environmentId = environmentId;
  }

  async getEvaluationDetails(
    agentId: string,
    runId: string,
    options: StudioEvaluationDetailsOptions = {},
  ) {
    const environmentId = options.environmentId ?? this.environmentId;
    if (
      !GUID_PATTERN.test(agentId) ||
      !GUID_PATTERN.test(runId) ||
      !ENVIRONMENT_PATTERN.test(environmentId)
    )
      throw failure("explicit environment, agent and run GUIDs are required.");
    if (
      options.includeResponseText !== undefined &&
      typeof options.includeResponseText !== "boolean"
    )
      throw failure("invalid text option.");
    if (
      options.maxResponseChars !== undefined &&
      (!Number.isSafeInteger(options.maxResponseChars) ||
        options.maxResponseChars < 1 ||
        options.maxResponseChars > 4000)
    )
      throw failure("maxResponseChars must be 1–4000.");
    const controller = new AbortController();
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const assertActive = () => {
      if (controller.signal.aborted) {
        body?.destroy();
        throw failure("deadline exceeded.");
      }
    };
    const work = async () => {
      const before = this.authProvider.getCurrentAccount();
      if (
        !before?.username ||
        !guid(before.tenantId) ||
        !this.authProvider.getCopilotStudioTokenSilent
      )
        throw failure(
          "management authentication unavailable for the selected account; no fallback was attempted.",
          401,
        );
      const selection = { username: before.username, tenantId: String(before.tenantId) };
      const priorOwner = guid(before.localAccountId);
      const env = await this.environmentApi.getEnvironment(environmentId, {
        boundedRead: true,
        signal: controller.signal,
      });
      assertActive();
      if (!object(env) || !sameId(env.name, environmentId) || !object(env.properties))
        throw failure("environment discovery identity mismatch.");
      const endpoints = env.properties.runtimeEndpoints;
      if (!object(endpoints))
        throw failure("Power Virtual Agents runtime endpoint is unavailable.");
      const endpoint = endpoints["microsoft.PowerVirtualAgents"];
      // Only the service-owned public-cloud management gateway family is supported.
      if (
        typeof endpoint !== "string" ||
        endpoint.length > 512 ||
        !/^https:\/\/powervamg\.[a-z0-9-]+\.gateway\.prod\.island\.powerapps\.com\/?$/.test(
          endpoint,
        )
      )
        throw failure("Power Virtual Agents runtime origin is unsupported or unsafe.");
      const origin = new URL(endpoint).origin;
      const selectedBeforeToken = this.authProvider.getCurrentAccount();
      if (
        !selectedBeforeToken ||
        selectedBeforeToken.username !== selection.username ||
        selectedBeforeToken.tenantId !== selection.tenantId ||
        (priorOwner !== null && selectedBeforeToken.localAccountId !== priorOwner)
      )
        throw failure("selected maker identity changed; no Studio token was requested.");
      let token: string;
      try {
        token = await this.authProvider.getCopilotStudioTokenSilent();
      } catch (error) {
        throw failure(
          `management authentication unavailable [${getSafeCopilotStudioManagementAuthReason(error)}] for the selected account; no fallback was attempted.`,
          401,
        );
      }
      assertActive();
      const account = this.authProvider.getCurrentAccount();
      if (
        !account ||
        account.username !== selection.username ||
        account.tenantId !== selection.tenantId ||
        !guid(account.localAccountId) ||
        (priorOwner !== null && account.localAccountId !== priorOwner) ||
        typeof token !== "string" ||
        !token.trim()
      )
        throw failure("selected maker identity changed or is unavailable.");
      const ownerId = String(account.localAccountId);
      const url = `${origin}/api/botmanagement/v2/environments/${encodeURIComponent(environmentId)}/bots/${agentId}/makerevaluations/${runId}/details`;
      const response = await requestWithRetry(
        url,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "x-cci-tenantid": selection.tenantId,
          },
          // Undici request has no redirect interceptor: 3xx is returned, never followed.
          signal: controller.signal,
          headersTimeout: DEADLINE_MS,
          bodyTimeout: DEADLINE_MS,
        },
        { maxAttempts: 1 },
      );
      body = response.body;
      assertActive();
      if (response.statusCode !== 200)
        throw failure(
          `service returned HTTP ${response.statusCode}; no fallback was attempted.`,
          response.statusCode,
        );
      const contentType = response.headers["content-type"];
      if (
        typeof contentType !== "string" ||
        !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(contentType)
      )
        throw failure("response is not JSON.");
      const chunks: Buffer[] = [];
      let size = 0;
      for await (const chunk of body as AsyncIterable<Uint8Array>) {
        assertActive();
        size += chunk.length;
        if (size > MAX_BYTES) throw failure("response exceeds 1 MiB.");
        chunks.push(Buffer.from(chunk));
      }
      assertActive();
      const current = this.authProvider.getCurrentAccount();
      if (
        !current ||
        current.username !== selection.username ||
        current.tenantId !== selection.tenantId ||
        current.localAccountId !== ownerId
      )
        throw failure("selected maker identity changed; response withheld.");
      let parsed: unknown;
      try {
        parsed = JSON.parse(Buffer.concat(chunks).toString("utf8"));
      } catch {
        throw failure("malformed JSON response.");
      }
      return normalize(parsed, environmentId, agentId, runId, ownerId, options);
    };
    try {
      // MSAL/CLI acquisition itself is not cancellable here. Check the shared signal
      // after every await so a late result cannot start another HTTP request.
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            body?.destroy();
            reject(failure("deadline exceeded."));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      if (error instanceof CopilotStudioEvaluationDetailsError) throw error;
      throw failure("read unavailable; no fallback was attempted.");
    } finally {
      clearTimeout(timer);
      controller.abort();
      body?.destroy();
    }
  }
}
